import { useState } from "react";
import { MOOD_LABELS, ENERGY_LABELS, prescriptionFor } from "../utils/mood";
// One vocabulary for saying today back — shared with the daily ritual so the
// two surfaces can never describe the same two numbers differently.
import { readbackLine, MOOD_WORD, ENERGY_WORD } from "../utils/ritual";
import { detectCrisis } from "../lib/crisis";
import CrisisSupport from "./CrisisSupport";
import { hapticLight } from "../utils/haptics";
import "../styles/home-surface.css";

const SCALE = [1, 2, 3, 4, 5];

/** Five marks, lit up to the level: the count carries the reading, so neither
 *  scale is ever encoded by hue alone. */
function Meter({ level, kind }) {
  return (
    <span className={`hs-meter hs-meter--${kind}`} aria-hidden="true">
      {SCALE.map((v) => (
        <span key={v} className="hs-meter-seg" data-on={v <= level} />
      ))}
    </span>
  );
}

/**
 * Mood: five discs on a dusk → daylight ramp. Unselected discs sit small and
 * washed out so the row reads as one gradient; the chosen one comes up to full
 * size and full colour. Never red-to-green — a low mood is not an error.
 */
function MoodScale({ value, onChange }) {
  return (
    <div className="ds-scale hs-mood-scale" role="group" aria-label="Mood">
      {SCALE.map((v) => (
        <button
          key={v}
          type="button"
          className="ds-scale-option"
          data-mood={v}
          aria-pressed={value === v}
          aria-label={`Mood: ${MOOD_LABELS[v - 1]}`}
          onClick={() => {
            hapticLight();
            onChange(v);
          }}
        >
          <span className="ds-scale-orb" data-mood={v} aria-hidden="true" />
          <span className="ds-scale-label">{MOOD_LABELS[v - 1]}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Energy: a level, so it gets a different physical metaphor — a meter that
 * fills from the left and takes the hue of the step you picked. Keeping the two
 * scales visually distinct stops them blurring into one question.
 */
function EnergyScale({ value, onChange }) {
  return (
    <div
      className="ds-scale hs-energy-scale"
      role="group"
      aria-label="Energy"
      data-level={value ?? 0}
    >
      {SCALE.map((v) => (
        <button
          key={v}
          type="button"
          className="ds-scale-option"
          data-energy={v}
          data-filled={value != null && v <= value}
          aria-pressed={value === v}
          aria-label={`Energy: ${ENERGY_LABELS[v - 1]}`}
          onClick={() => {
            hapticLight();
            onChange(v);
          }}
        >
          <span className="hs-bar" data-step={v} aria-hidden="true" />
          <span className="ds-scale-label">{ENERGY_LABELS[v - 1]}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * The 10-second daily check-in. Two taps is the entire required interaction —
 * anything heavier and people stop doing it, and without daily data the whole
 * evidence loop dies.
 *
 * Captures valence (mood) and arousal (energy) separately because they imply
 * different prescriptions: "flat and drained" and "wired and miserable" are both
 * low mood but need opposite sessions.
 */
export default function MoodCheckIn({ existing, onSave, onOpenNote }) {
  const [mood, setMood] = useState(existing?.mood ?? null);
  const [energy, setEnergy] = useState(existing?.energy ?? null);
  const [note, setNote] = useState(existing?.note ?? "");
  const [showNote, setShowNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!existing);

  const noteHasCrisis = detectCrisis(note);
  const canSave = mood != null && energy != null;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave({ mood, energy, note: note.trim() || null, context: "daily" });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  // Already checked in today — read it back calmly, with the movement that
  // suits it, and an easy way to change your mind.
  if (saved && !showNote) {
    const p = prescriptionFor(mood, energy);
    return (
      <section className="card card--hero ds-stack" aria-label="Today's check-in">
        <div className="ds-row-between">
          <p className="section-label" style={{ marginBottom: 0 }}>
            How you are today
          </p>
          <button type="button" className="ds-link" onClick={() => setSaved(false)}>
            Change
          </button>
        </div>

        {/* How they actually feel is the display moment of this screen — one
            sentence at display size, with the two values kept underneath it as
            small supporting detail rather than set twice at 30px as a pair of
            side-by-side statistics. */}
        <div className="hs-readback" data-mood={mood} data-energy={energy}>
          <p className="hs-readback-line">{readbackLine(mood, energy)}</p>
          <p className="hs-readback-detail">
            <span className="hs-readback-pair">
              <Meter level={mood} kind="mood" />
              Mood {MOOD_WORD[mood - 1]}
            </span>
            <span className="hs-readback-sep" aria-hidden="true">
              ·
            </span>
            <span className="hs-readback-pair">
              <Meter level={energy} kind="energy" />
              Energy {ENERGY_WORD[energy - 1]}
            </span>
          </p>
        </div>

        <div className="ds-well">
          <p className="hs-key">What would suit today</p>
          <p className="hs-prescription-what">{p.label}</p>
          <p className="hs-prescription-why">{p.why}</p>
        </div>

        {noteHasCrisis && <CrisisSupport />}
      </section>
    );
  }

  return (
    <section className="card card--hero" aria-label="Daily check-in">
      <div className="hs-checkin-intro">
        <h2 className="ds-title hs-checkin-question">How are you today?</h2>
        <p className="ds-support">No wrong answer. Ten seconds is plenty.</p>
      </div>

      <div className="hs-scale-group">
        <span className="hs-scale-label">Mood</span>
        <MoodScale value={mood} onChange={setMood} />
      </div>

      <div className="hs-scale-group">
        <span className="hs-scale-label">Energy</span>
        <EnergyScale value={energy} onChange={setEnergy} />
      </div>

      {/* Answering both questions gets an immediate, proportionate reply —
          the app responds to you before it asks anything of you. */}
      {canSave && (
        <div
          className="ds-well hs-prescription"
          key={prescriptionFor(mood, energy).key}
          style={{ marginTop: "var(--stack-lg)" }}
        >
          <p className="hs-key">What would suit today</p>
          <p className="hs-prescription-what">{prescriptionFor(mood, energy).label}</p>
          <p className="hs-prescription-why">{prescriptionFor(mood, energy).why}</p>
        </div>
      )}

      {showNote && (
        <div style={{ marginTop: "var(--stack-lg)" }}>
          <label>
            <span className="hs-field-label">Anything on your mind? Optional.</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="However today has actually been."
              rows={3}
            />
          </label>
          {noteHasCrisis && <CrisisSupport />}
        </div>
      )}

      <button
        className="btn btn-primary btn-full"
        style={{ marginTop: "var(--stack-lg)" }}
        onClick={handleSave}
        disabled={!canSave || saving}
      >
        {saving ? "Saving" : "Save how I'm doing"}
      </button>

      {!showNote && (
        <button
          type="button"
          className="ds-link"
          style={{ display: "block", margin: "var(--space-2) auto 0" }}
          onClick={() => {
            setShowNote(true);
            onOpenNote?.();
          }}
        >
          Add a note
        </button>
      )}
    </section>
  );
}
